import { createBatchedParamsArrayFromIds, creatorbase } from '@round/api';
import { useCreatorbaseTiktokAudiosListQueries, useTiktokPostStats } from '@round/creatorbase-helpers/api-hooks';
import { TableMeta, TikTokTopPostsTableRow } from './types';
import { useMemo } from 'react';

// A song can carry hundreds of audios, and naming every one of them in the query string puts the
// request line past the ~4094 bytes gunicorn accepts. It rejects it before Django sees it, which
// reaches the browser as a CORS error rather than anything readable.
const AUDIO_BATCH_SIZE = 50;

const buildAudioBatchParams = (ids: number[]) =>
    createBatchedParamsArrayFromIds(ids, (batch) => ({ id: batch.join(',') }), AUDIO_BATCH_SIZE);

type Params = {
    audioIds: number[];
    shouldFetchAudioData?: boolean;
} & Pick<creatorbase.PostStatsPostData, 'page' | 'page_size' | 'ordering'>;

export default function useTikTokTopPosts({ audioIds, page, page_size, ordering, shouldFetchAudioData }: Params) {
    const postStats = useTiktokPostStats(
        {
            audio_ids: audioIds,
            ordering,
            page_size,
            page,
        },
        {
            // to preserve count on pagination
            placeholderData: (query) =>
                query
                    ? {
                          ...query,
                          results: [],
                      }
                    : undefined,
        }
    );

    /*
     * There are currently 2 contexts where we display top posts table:
     * - top posts for an audio
     * - top posts for a song
     * It doesn't make sense to fetch and display audio data in the first case.
     * But given everything else is currently shared I thought a condition wouldn't hurt much.
     * Conceptually both cases seem the same table apart from supplementary data
     * */
    const isAudioDataEnabled = !!audioIds.length && !!shouldFetchAudioData;

    const audioBatchParams = buildAudioBatchParams(audioIds);
    const audioBatchArgs = audioBatchParams.map((params) => ({
        params,
        config: { enabled: isAudioDataEnabled },
    }));

    const { audiosById, isAudiosLoading } = useCreatorbaseTiktokAudiosListQueries(audioBatchArgs, {
        combine: (results) => ({
            audiosById: new Map(
                results.flatMap((result) => result.data?.results ?? []).map((audio) => [audio.id, audio])
            ),
            isAudiosLoading: results.some((result) => result.isLoading),
        }),
    });

    const rows: TikTokTopPostsTableRow[] = useMemo(
        () =>
            postStats.data?.results.map((postStats) => ({
                ...postStats,
                audio: audiosById.get(postStats.audio_id) ?? null,
            })) ?? [],
        [postStats.data?.results, audiosById]
    );

    const meta: TableMeta = {
        isLoading: !!audioIds.length && (postStats.isPending || postStats.isFetching),
        getIsAudioLoading: (audioId) => {
            return !audiosById.has(audioId) && isAudiosLoading;
        },
    };

    return {
        rows,
        count: postStats.data?.count,
        status: postStats.status,
        meta,
    };
}
